import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Flame, X } from 'lucide-react';
import useHeatmap, { DATA_LAYERS, PRESETS } from '@/hooks/useHeatmap';
import useFeatures from '@/hooks/useFeatures';

const GLASS_STYLE = {
  background: 'rgba(255,255,255,0.88)',
  backdropFilter: 'blur(40px)',
  WebkitBackdropFilter: 'blur(40px)',
};

const DARK_GLASS_STYLE = {
  background: 'rgba(15,23,42,0.88)',
  backdropFilter: 'blur(40px)',
  WebkitBackdropFilter: 'blur(40px)',
};

const PRESET_OPTIONS = [
  { id: 'today', labelKey: 'heatmap.today', fallback: 'Today' },
  { id: 'yesterday', labelKey: 'heatmap.yesterday', fallback: 'Yesterday' },
  { id: '7days', labelKey: 'heatmap.7days', fallback: '7 Days' },
  { id: '30days', labelKey: 'heatmap.30days', fallback: '30 Days' },
  { id: 'custom', labelKey: 'heatmap.custom', fallback: 'Custom' },
];

export default function HeatmapControl({ mapRef }) {
  const { t } = useTranslation();
  const features = useFeatures();
  const devices = useSelector((state) => state.devices.items);
  const { generate, clear, loading, geojson, stats } = useHeatmap();

  const staffByUserId = useSelector((state) => state.staff?.byUserId ?? {});

  const [active, setActive] = useState(false);
  const [open, setOpen] = useState(false);
  const [layer, setLayer] = useState('positions');
  // Combined target value:
  //   '' → All (vehicles + staff)
  //   'v:<deviceId>' → a vehicle
  //   's:<userId>' → a staff user
  const [target, setTarget] = useState('');
  const [preset, setPreset] = useState('today');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [intensity, setIntensity] = useState(75);

  // Active target kind: 'vehicle' | 'staff' | 'all'. Drives which layer
  // pills are shown (vehicle-only layers hide when a staff user is picked,
  // and vice versa).
  const targetKind = target.startsWith('v:') ? 'vehicle' : target.startsWith('s:') ? 'staff' : 'all';

  // Filter data layers based on feature toggles + current target kind.
  const visibleLayers = DATA_LAYERS.filter((l) => {
    if (l.id === 'fuel' && !features.fuel) return false;
    if (l.id === 'alerts' && !features.alerts) return false;
    if (targetKind === 'vehicle' && l.target === 'staff') return false;
    if (targetKind === 'staff' && l.target === 'vehicle') return false;
    return true;
  });

  // If the currently-selected layer is hidden by the new target, snap back
  // to "positions" (the only universal layer) — otherwise the user is
  // looking at a phantom active layer that won't render in the dropdown.
  useEffect(() => {
    if (!visibleLayers.some((l) => l.id === layer)) {
      setLayer('positions');
    }
  }, [layer, visibleLayers]);

  // Push geojson to map when available
  useEffect(() => {
    if (active && geojson) {
      mapRef.current?.setHeatmapData(geojson);
    }
  }, [geojson, active, mapRef]);

  const handleToggle = () => {
    if (active) {
      mapRef.current?.setHeatmapData(null);
      setActive(false);
      setOpen(false);
      clear();
    } else {
      setOpen(!open);
    }
  };

  const handleIntensity = useCallback((e) => {
    const val = Number(e.target.value);
    setIntensity(val);
    mapRef.current?.setHeatmapIntensity(val / 100);
  }, [mapRef]);

  const handleGenerate = async () => {
    let from, to;
    if (preset === 'custom') {
      if (!customFrom || !customTo) return;
      from = new Date(customFrom).toISOString();
      to = new Date(customTo).toISOString();
    } else {
      const range = PRESETS[preset]();
      from = range.from;
      to = range.to;
    }

    const kind = target.startsWith('v:') ? 'vehicle'
      : target.startsWith('s:') ? 'staff'
      : 'all';
    const id = target.includes(':') ? target.slice(2) : null;

    await generate({
      layer,
      deviceId: kind === 'vehicle' ? id : null,
      userId: kind === 'staff' ? id : null,
      from,
      to,
    });
    setActive(true);
    setOpen(false);
  };

  const deviceList = Object.values(devices)
    .filter((d) => !d.hidden_from_default_list)
    .sort((a, b) => a.name.localeCompare(b.name));
  const staffList = Object.values(staffByUserId)
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  return (
    <div className="relative">
      {/* Toggle button */}
      <button
        onClick={handleToggle}
        className={`h-9 px-3.5 text-xs font-semibold rounded-xl flex items-center gap-2 transition-all shadow-md ${
          active
            ? 'bg-red-500 text-white shadow-red-500/25'
            : 'bg-white/90 text-slate-600 hover:bg-white border border-slate-200/80 dark:bg-slate-800/90 dark:text-slate-300 dark:border-slate-700/80 dark:hover:bg-slate-800'
        }`}
        style={{ backdropFilter: 'blur(12px)' }}
        title={t('heatmap.title', 'Fleet Heatmap')}
      >
        <Flame className="w-3.5 h-3.5" />
        {t('heatmap.short', 'Heatmap')}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div
          className="absolute top-11 end-0 z-50 w-[280px] rounded-2xl shadow-xl border border-white/30 dark:border-slate-700/50 p-4 space-y-4"
          style={GLASS_STYLE}
        >
          {/* Dark mode overlay */}
          <div className="hidden dark:block absolute inset-0 rounded-2xl pointer-events-none" style={DARK_GLASS_STYLE} />

          <div className="relative z-10 space-y-4">
            {/* Close button */}
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="absolute top-0 end-0 p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:text-slate-300 dark:hover:bg-slate-700 transition-all z-20"
            >
              <X size={14} />
            </button>

            {/* Data layer selector */}
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-1.5 block">
                {t('heatmap.dataLayer', 'Data Layer')}
              </label>
              <div className="flex flex-wrap gap-1.5">
                {visibleLayers.map((l) => (
                  <button
                    key={l.id}
                    onClick={() => setLayer(l.id)}
                    className={`px-2.5 py-1 text-[11px] font-medium rounded-lg transition-all ${
                      layer === l.id
                        ? 'bg-blue-500 text-white shadow-sm'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600'
                    }`}
                  >
                    {t(l.labelKey, l.id.charAt(0).toUpperCase() + l.id.slice(1))}
                  </button>
                ))}
              </div>
            </div>

            {/* Target selector (vehicles + staff). The non-positions data
                layers (alerts/fuel/stops) are vehicle-specific, so staff
                options stay visible but selecting one with a non-positions
                layer returns an empty result by design. */}
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-1.5 block">
                {t('heatmap.target', 'Target')}
              </label>
              <select
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                className="w-full h-8 px-2.5 text-xs rounded-lg border border-slate-200/80 bg-white/60 text-slate-700 dark:bg-slate-800/60 dark:border-slate-600 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
              >
                <option value="">{t('heatmap.allAssets', 'All assets')}</option>
                {deviceList.length > 0 && (
                  <optgroup label={t('heatmap.vehicles', 'Vehicles')}>
                    {deviceList.map((d) => (
                      <option key={`v:${d.id}`} value={`v:${d.id}`}>{d.name}</option>
                    ))}
                  </optgroup>
                )}
                {staffList.length > 0 && (
                  <optgroup label={t('heatmap.staffSection', 'People')}>
                    {staffList.map((s) => (
                      <option key={`s:${s.userId}`} value={`s:${s.userId}`}>
                        {s.name || `User ${String(s.userId).slice(0, 8)}`}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
            </div>

            {/* Time range presets */}
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-1.5 block">
                {t('heatmap.timeRange', 'Time Range')}
              </label>
              <div className="flex flex-wrap gap-1.5">
                {PRESET_OPTIONS.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setPreset(p.id)}
                    className={`px-2.5 py-1 text-[11px] font-medium rounded-lg transition-all ${
                      preset === p.id
                        ? 'bg-blue-500 text-white shadow-sm'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600'
                    }`}
                  >
                    {t(p.labelKey, p.fallback)}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom date inputs */}
            {preset === 'custom' && (
              <div className="flex gap-2">
                <input
                  type="datetime-local"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  className="flex-1 h-8 px-2 text-[11px] rounded-lg border border-slate-200/80 bg-white/60 text-slate-700 dark:bg-slate-800/60 dark:border-slate-600 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                />
                <input
                  type="datetime-local"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  className="flex-1 h-8 px-2 text-[11px] rounded-lg border border-slate-200/80 bg-white/60 text-slate-700 dark:bg-slate-800/60 dark:border-slate-600 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                />
              </div>
            )}

            {/* Intensity slider */}
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-1.5 block">
                {t('heatmap.intensity', 'Intensity')} — {intensity}%
              </label>
              <input
                type="range"
                min="0"
                max="100"
                value={intensity}
                onChange={handleIntensity}
                className="w-full h-1.5 bg-slate-200 rounded-full appearance-none cursor-pointer dark:bg-slate-600 accent-blue-500"
              />
            </div>

            {/* Stats row */}
            {active && stats.points > 0 && (
              <div className="flex items-center gap-3 text-[11px] text-slate-500 dark:text-slate-400">
                <span>{stats.points.toLocaleString()} {t('heatmap.dataPoints', 'data points')}</span>
                <span className="text-slate-300 dark:text-slate-600">|</span>
                <span>{stats.vehicles} {t('heatmap.vehicles', 'vehicles')}</span>
              </div>
            )}

            {/* Generate button */}
            <button
              onClick={handleGenerate}
              disabled={loading || (preset === 'custom' && (!customFrom || !customTo))}
              className="w-full h-9 text-xs font-semibold rounded-xl bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {t('heatmap.generating', 'Generating...')}
                </>
              ) : (
                <>
                  <Flame className="w-3.5 h-3.5" />
                  {t('heatmap.generate', 'Generate Heatmap')}
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Color legend */}
      {active && geojson?.features?.length > 0 && (
        <div
          className="absolute top-11 end-0 z-10 px-3 py-1.5 rounded-xl shadow-md flex items-center gap-2 text-[10px] font-medium text-slate-500 dark:text-slate-400 border border-white/30 dark:border-slate-700/50"
          style={GLASS_STYLE}
        >
          <span>{t('heatmap.low', 'Low')}</span>
          <div
            className="w-16 h-2 rounded-full"
            style={{
              background: 'linear-gradient(to right, rgba(59,130,246,0.4), rgba(245,158,11,0.7), rgba(239,68,68,0.9))',
            }}
          />
          <span>{t('heatmap.high', 'High')}</span>
        </div>
      )}
    </div>
  );
}
